import * as React from 'react'

import {Flex} from '@ua/design-system/components/flex'
import {Spinner} from '@ua/design-system/components/spinner'

import getUrl, {APP_KEY} from 'airship/modules/get-url'
import pageContext from 'airship/modules/page-context'
import * as waffle from 'airship/modules/waffle'
import WaffleFlag from 'airship/modules/waffle/flags'
import Badge from 'airship/patterns/badge'
import Modal from 'airship/patterns/modal'
import SvgSprite from 'airship/patterns/svg-sprite'
import WorkflowList from 'airship/widgets/new-push-popover/workflow-list'

import JourneyTemplateList from './journey-template-list'
import MessageType from './message-type'
import type {CurrentCompany} from './types'
import {buildGetAccessLink, getUrlWithCreationSourceHints} from './utils'

import classnames = require('classnames')

const BODY_CLASS = 'modal-open'

export interface Props {
  hasContentAuthorAccess: boolean
  hasPComposerAccess: boolean
  hasExperimentsAccess: boolean
  hasAppleNews: boolean
  hasJourneys: boolean
  hasTriggersTag: boolean
  hasInAppAutomation: boolean
  hasTemplates: boolean
  hasScene: boolean
  hasWebScene: boolean
  hasSurvey: boolean
}

interface PageContext {
  context_processors: {
    current_company: CurrentCompany
  }
}

const NewPushPopover: React.FC<Props> = (props) => {
  const [open, setOpen] = React.useState(false)
  const [creating, setCreating] = React.useState(false)

  const context = pageContext<PageContext>()
  const createMessageUrl = getUrlWithCreationSourceHints(
    'apps_composer_create_goatzart_message',
    APP_KEY,
    'normal'
  )

  const createExperimentUrl = getUrlWithCreationSourceHints(
    'apps_composer_create_goatzart_message',
    APP_KEY,
    'experiment'
  )
  const createAppleNewsUrl = getUrl('apple_news:apple_news_composer', APP_KEY)
  const createAutomatedUrl = getUrlWithCreationSourceHints(
    'apps_composer_create_goatzart_message',
    APP_KEY,
    'automated'
  )
  const createZoltarUrl = getUrlWithCreationSourceHints(
    'apps_composer_create_goatzart_message',
    APP_KEY,
    'zoltar'
  )

  const createAppSurveyUrl = getUrlWithCreationSourceHints(
    'apps_composer_create_goatzart_message',
    APP_KEY,
    'app_survey'
  )

  const createAppExperienceUrl = getUrlWithCreationSourceHints(
    'apps_composer_create_goatzart_message',
    APP_KEY,
    'app_experience'
  )
  const createTemplatesUrl = `${getUrl(
    'content_templates_nav:app_content_templates_index',
    APP_KEY
  )}create`
  const createJourneyModalOpenUrl = getUrlWithCreationSourceHints(
    'apps:apps_journeys_hq_map_create',
    APP_KEY
  )

  const handleGetAccessClick = (): void => {
    const urlToBeRedirected = buildGetAccessLink(
      context.context_processors.current_company
    )

    window.location.href = urlToBeRedirected
  }

  // To prevent double creates, we remove the href once we've started creating a new message
  const conditionalHref = (href: string): string | undefined => {
    return creating ? undefined : href
  }

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (document.body.classList) {
      document.body.classList.toggle(BODY_CLASS, open)
    }

    return () => {
      document.body.classList.remove(BODY_CLASS)
    }
  }, [open])

  return (
    <>
      {(props.hasPComposerAccess || props.hasContentAuthorAccess) && (
        <CreateNewButton />
      )}
      <Modal isOpen={open} onClose={toggle}>
        {(props.hasPComposerAccess || props.hasContentAuthorAccess) && (
          <React.Fragment>
            <StandardExperienceTypes />
            <StandardMessageTypes />
            {props.hasJourneys && (
              <JourneyTemplateList title="Sequence Templates" />
            )}
            <WorkflowList />
            {creating && (
              <Flex direction="column" alignItems="center">
                <Spinner />
              </Flex>
            )}
          </React.Fragment>
        )}
      </Modal>
    </>
  )

  function toggle(): void {
    setOpen(!open)
    // Remove the loading spinner after closing the modal. This prevents a permanent
    // loading state after clicking a message type via "open in a new tab".
    setCreating(false)
  }

  function StandardMessageTypes(): JSX.Element {
    return (
      <>
        <div className="u-contain-spread-sm u-space-ends-6 u-contain-spread-sm u-space-ends-6">
          <h2 className="u-flex u-flex-justify-between u-pad-top-2 u-text-shrink-1 u-text-uppercase u-text-bold u-grey-60">
            {'Messages'}
          </h2>
          <div className="create-grid">
            <MessageType
              href={conditionalHref(createMessageUrl)}
              onClick={() => setCreating(true)}
              orientation="horizontal"
              icon="composer-message"
              title="Message"
              description="Send or schedule a message."
              data-selenium-target="composer-create-message"
            />

            {props.hasExperimentsAccess && (
              <MessageType
                href={conditionalHref(createExperimentUrl)}
                onClick={() => setCreating(true)}
                orientation="horizontal"
                icon="composer-ab-test"
                title="A/B Test"
                description="Experiment with different variations of a message."
              />
            )}
            {props.hasTriggersTag && (
              <MessageType
                href={conditionalHref(createAutomatedUrl)}
                onClick={() => setCreating(true)}
                orientation="horizontal"
                icon="composer-automation"
                title="Automation"
                description="Automatically send a message to users who meet certain conditions."
              />
            )}
            {props.hasJourneys && (
              <MessageType
                href={conditionalHref(createJourneyModalOpenUrl)}
                onClick={() => setCreating(true)}
                orientation="horizontal"
                icon="composer-series"
                title="Sequence"
                description="Automatically send a series of messages to users who meet certain conditions."
              />
            )}
            {props.hasInAppAutomation && (
              <MessageType
                href={conditionalHref(createZoltarUrl)}
                onClick={() => setCreating(true)}
                orientation="horizontal"
                icon="composer-inapp-automation"
                title="In-App Automation"
                description="Create a message that appears in your app when users meet certain conditions."
              />
            )}
            {props.hasSurvey &&
              !waffle.flag_is_active(WaffleFlag.LegacySurveyMigration) && (
                <MessageType
                  href={conditionalHref(createAppSurveyUrl)}
                  onClick={() => setCreating(true)}
                  orientation="horizontal"
                  icon="composer-inapp-survey"
                  title="Survey"
                  description="Create a survey that appears in your app when users meet certain conditions."
                  badge={
                    waffle.flag_is_active(WaffleFlag.RecommendedOptionBadge) ? (
                      <Badge
                        displayClasses="u-space-left-05 u-pad-sides-03 u-text-regular"
                        foreground="black"
                        background="grey-40"
                        style="pill"
                        size="sm"
                      >
                        Deprecated
                      </Badge>
                    ) : undefined
                  }
                />
              )}
            {props.hasTemplates && (
              <MessageType
                href={conditionalHref(createTemplatesUrl)}
                onClick={() => setCreating(true)}
                orientation="horizontal"
                icon="composer-template"
                title="Template"
                description="Create reusable templates with personalized fields for each audience member."
              />
            )}
            {props.hasAppleNews && (
              <MessageType
                href={conditionalHref(createAppleNewsUrl)}
                onClick={() => setCreating(true)}
                orientation="horizontal"
                icon="composer-applenews"
                title="Apple News"
                description="Send a push notification that links to an Apple News story."
              />
            )}
          </div>
        </div>
      </>
    )
  }

  function StandardExperienceTypes(): JSX.Element {
    return (
      <>
        <div className="u-contain-spread-sm u-space-ends-6 u-contain-spread-sm u-space-ends-6">
          <h1 className="u-text-center u-space-ends-6">
            What would you like to create?
          </h1>

          <h2 className="u-flex u-flex-justify-between u-pad-top-2 u-text-shrink-1 u-text-uppercase u-text-bold u-grey-60">
            {'Experiences'}
          </h2>
          <div className="create-grid">
            <MessageType
              href={
                props.hasScene
                  ? conditionalHref(createAppExperienceUrl)
                  : undefined
              }
              onClick={props.hasScene ? () => setCreating(true) : undefined}
              orientation="horizontal"
              icon="composer-inapp-tour"
              title="Scene"
              description={
                props.hasWebScene
                  ? ' Create a single or multi-screen experience that appears in your app or on your website when users meet certain conditions.'
                  : ' Create a single or multi-screen experience that appears in your app when users meet certain conditions.'
              }
              blocked={!props.hasScene}
              badge={
                waffle.flag_is_active(WaffleFlag.RecommendedOptionBadge) ? (
                  <Badge
                    displayClasses="u-space-left-05 u-pad-sides-03 u-text-regular"
                    foreground="blue"
                    background="blue-20"
                    style="pill"
                    size="sm"
                  >
                    Recommended
                  </Badge>
                ) : undefined
              }
            >
              {!props.hasScene && (
                <button
                  className="u-blue u-text-underline"
                  onClick={handleGetAccessClick}
                >
                  {' '}
                  Get access
                </button>
              )}
            </MessageType>
          </div>
        </div>
      </>
    )
  }

  function CreateNewButton(): JSX.Element {
    return (
      <button
        className={classnames('global-header__primary-action-link', {
          active: open,
        })}
        onClick={toggle}
        aria-label="create"
        data-selenium-target="composer-create-dropdown"
      >
        <SvgSprite name="add" />
      </button>
    )
  }
}

export default NewPushPopover
